import flatpickr from 'flatpickr';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { createFooterButtons } from '@/datetime/flatpickr/flatpickrButtons.util';
import { positionFlatpickrCalendar } from '@/datetime/flatpickr/flatpickrPositioning.util';
import {
  createDateWithNewMonthYear,
  momentToTimezoneNaiveDate,
  timezoneNaiveDateToMoment,
} from '@/datetime/flatpickr/flatpickrTimezones.util';
import type { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import type { Moment } from 'moment-timezone';
import type { RefObject } from 'react';
import type { CreateFlatpickrFooter } from '@/datetime/flatpickr/flatpickrButtons.util';
import type { Timezone } from '@/datetime/timezone.service';

const INITIALIZATION_TIMEOUT_MS = 2000;

const warnFlatpickrIssue = (message: string, details: Record<string, unknown> = {}): void => {
  console.warn(`[useFlatpickr] ${message}`, details);
};

/** Discards any pending calendar selection by showing the last committed value again. */
const resetToCommittedValue = (
  instance: FlatpickrInstance,
  committedValue: Moment | null | undefined,
  timezone: Timezone,
): void => {
  if (committedValue) {
    instance.setDate(momentToTimezoneNaiveDate(committedValue, timezone), false);
  } else {
    instance.clear(false);
  }
};

export interface UseFlatpickrParameters {
  /**
   * Input element to attach flatpickr to
   */
  anchorRef: React.RefObject<HTMLElement | null>;

  /**
   * Current datetime value
   */
  value: Moment | null | undefined;

  /**
   * The tz to be used for datetime value
   */
  timezone: Timezone;

  /**
   * Format string used to display the datetime value
   */
  localizedDateFormat: string;

  /**
   * Callback to handle date selection
   * @param date The selected date
   */
  onDateSelect: (date: Moment | undefined) => void;

  /**
   * Callback to handle manual input
   * @param input The input value
   * @returns The parsed date or undefined if parsing fails
   */
  onManualInput?: (input: string) => Moment | undefined;

  /**
   * Flag to enable the seconds input
   */
  enableSeconds?: boolean;

  /**
   * Flag to enable the time input
   */
  enableTime?: boolean;

  /**
   * Flag to display time in 24-hour format
   */
  enableTime24hr?: boolean;

  /**
   * Flag to indicate if the picker is used on a modal
   */
  isInsideModal?: boolean;

  /**
   * The minimum date that a user can start picking from (inclusive).
   */
  minDate?: Moment;

  /**
   * The maximum date that a user can pick to (inclusive).
   */
  maxDate?: Moment;

  /**
   * Adjusts the step for the minute input
   */
  minuteIncrement?: number;

  /**
   * Flag to render the picker inline
   */
  inline?: boolean;

  /**
   * Flag to control whether the flatpickr is open. If undefined, the picker will be open by default.
   */
  isOpen?: boolean;

  /**
   * Function to render the footer of the flatpickr instance.
   */
  renderFooter?: CreateFlatpickrFooter;
  /**
   * Flag to control whether the date should be dynamically updated when only month/year changes in the picker.
   * When false, clicking "Done" will not auto-update the selected date to match the current month/year.
   * Defaults to true.
   */
  autoUpdateDate?: boolean;
  /**
   * When true, picking a date or time in the calendar only updates the calendar itself; the selection is passed to
   * `onDateSelect` when the user clicks "Done", while "Cancel" discards it. Typed input also waits for "Done";
   * closing the calendar or clicking elsewhere discards pending changes.
   * Defaults to false, which applies every calendar selection immediately.
   */
  applyOnDone?: boolean;
  calendarExtraClassName?: string;
  /**
   * Optional external lifecycle signal that should trigger another render/initialization attempt.
   * Used by containers such as AG Grid that can render a React portal before attaching its GUI to the DOM.
   */
  initializationSignal?: unknown;

  /**
   * Callback fired when the flatpickr calendar opens.
   */
  onOpen?: () => void;

  /**
   * Callback fired when the flatpickr calendar closes, regardless of the reason (Done, Cancel, or clicking away).
   */
  onClose?: () => void;

  /**
   * Callback fired when the user clicks the footer "Done" button.
   */
  onDoneClick?: () => void;

  /**
   * Callback fired when the user clicks the footer "Cancel" button.
   */
  onCancelClick?: () => void;
}

export const useFlatpickr = ({
  anchorRef,
  value,
  timezone,
  localizedDateFormat,
  enableSeconds,
  enableTime,
  enableTime24hr,
  onDateSelect,
  onManualInput,
  isInsideModal,
  minDate,
  maxDate,
  minuteIncrement = 1,
  inline,
  isOpen,
  renderFooter,
  autoUpdateDate = true,
  applyOnDone = false,
  calendarExtraClassName,
  initializationSignal,
  onOpen,
  onClose,
  onDoneClick,
  onCancelClick,
}: UseFlatpickrParameters): RefObject<FlatpickrInstance | null> => {
  const { t } = useTranslation();
  const flatpickrRef = useRef<FlatpickrInstance | null>(null);
  const buttonCleanupRef = useRef<(() => void) | null>(null);

  // Footer Done/Cancel and flatpickr's own lifecycle callbacks are created once in onReady/init;
  // keep latest values in refs so they never go stale.
  const onDateSelectRef = useRef(onDateSelect);
  const onManualInputRef = useRef(onManualInput);
  const valueRef = useRef(value);
  const timezoneRef = useRef(timezone);
  const autoUpdateDateRef = useRef(autoUpdateDate);
  const applyOnDoneRef = useRef(applyOnDone);
  const onOpenRef = useRef(onOpen);
  const onCloseRef = useRef(onClose);
  const onDoneClickRef = useRef(onDoneClick);
  const onCancelClickRef = useRef(onCancelClick);
  onDateSelectRef.current = onDateSelect;
  onManualInputRef.current = onManualInput;
  valueRef.current = value;
  timezoneRef.current = timezone;
  autoUpdateDateRef.current = autoUpdateDate;
  applyOnDoneRef.current = applyOnDone;
  onOpenRef.current = onOpen;
  onCloseRef.current = onClose;
  onDoneClickRef.current = onDoneClick;
  onCancelClickRef.current = onCancelClick;

  const formatDate = useCallback(
    (date: Date, _format?: string): string => {
      const targetMoment = timezoneNaiveDateToMoment(date, timezone);
      return targetMoment.format(localizedDateFormat);
    },
    [timezone, localizedDateFormat],
  );

  const parseDate = useCallback(
    (dateString: string, _format?: string, _timeless?: boolean): Date => {
      // Flatpickr also parses on blur, Enter, and outside clicks. In Done mode those paths must not invoke
      // onManualInput, which commits the parsed value. Done handles edited text explicitly instead.
      if (applyOnDoneRef.current) {
        return flatpickrRef.current?.selectedDates[0] ?? new Date();
      }
      const parsedMoment = onManualInput?.(dateString);
      if (!parsedMoment) {
        // Return a default date if parsing fails
        return new Date();
      }
      const timezoneNaiveDate = momentToTimezoneNaiveDate(parsedMoment, timezone);
      flatpickrRef.current?.setDate(timezoneNaiveDate, false);
      return timezoneNaiveDate;
    },
    [onManualInput, timezone],
  );

  const handleReady = useCallback(
    (_dates: Date[], _dateStr: string, instance: FlatpickrInstance) => {
      if (typeof renderFooter === 'function') {
        renderFooter({
          flatpickrInstance: instance,
        });
        return;
      }
      const onCancel = () => {
        onCancelClickRef.current?.();
        resetToCommittedValue(instance, valueRef.current, timezoneRef.current);
        instance.close();
      };

      const onDone = () => {
        onDoneClickRef.current?.();
        if (applyOnDoneRef.current) {
          const input = instance.input.value;
          const selectedDate = instance.selectedDates[0];
          const selectedText = selectedDate ? instance.formatDate(selectedDate, instance.config.dateFormat) : '';
          if (!input.trim()) {
            onDateSelectRef.current(undefined);
            instance.close();
            return;
          }
          if (input !== selectedText && onManualInputRef.current) {
            onManualInputRef.current(input);
            instance.close();
            return;
          }
        }
        const commitsOnDone = autoUpdateDateRef.current || applyOnDoneRef.current;
        if (commitsOnDone) {
          const selectedDate = instance.selectedDates?.[0];
          if (!selectedDate) {
            return;
          }

          const dateToCommit = autoUpdateDateRef.current
            ? createDateWithNewMonthYear(selectedDate, instance.currentYear, instance.currentMonth)
            : selectedDate;
          onDateSelectRef.current(timezoneNaiveDateToMoment(dateToCommit, timezoneRef.current));
        }
        instance.close(); // also fires the onClose config hook below, so onDoneClick is followed by onClose
      };
      buttonCleanupRef.current = createFooterButtons({ flatpickrInstance: instance, t, onCancel, onDone });
    },
    [renderFooter, t],
  );

  const handleDateChange = useCallback(
    (selectedDates: Date[]) => {
      if (applyOnDone) return;
      if (selectedDates.length > 0) {
        const selectedDate = selectedDates[0];
        const zonedDate = timezoneNaiveDateToMoment(selectedDate, timezone);
        onDateSelect(zonedDate);
      } else {
        // No dates selected - clear the date
        onDateSelect(undefined);
      }
    },
    [applyOnDone, onDateSelect, timezone],
  );

  /**
   * Discards a pending selection when the calendar closes without "Done" (e.g. Escape). This is only safe because
   * `closeOnSelect` is off in applyOnDone mode: flatpickr closes *before* firing onChange for a day click, so
   * resetting on close in the default mode would make that onChange commit the previous date.
   */
  const handleClose = useCallback(
    (_selectedDates: Date[], _dateStr: string, instance: FlatpickrInstance) => {
      if (applyOnDoneRef.current) {
        resetToCommittedValue(instance, valueRef.current, timezoneRef.current);
      }
      anchorRef.current?.blur();
      onCloseRef.current?.();
    },
    [anchorRef],
  );

  /**
   * Updates the visual selection when navigating months/years.
   * This provides immediate visual feedback showing which date will be selected
   * when the user clicks "Done" after navigating to a different month/year.
   */
  const handleMonthYearChange = useCallback(
    (_selectedDates: Date[], _dateStr: string, instance: FlatpickrInstance) => {
      if (!autoUpdateDate) return;

      const currentSelection = instance.selectedDates?.[0];
      if (!currentSelection) return;

      const newDate = createDateWithNewMonthYear(currentSelection, instance.currentYear, instance.currentMonth);

      // Update selection visually without triggering onChange
      instance.setDate(newDate, false);
    },
    [autoUpdateDate],
  );

  const getModalContainer = useCallback((): HTMLElement | null => {
    return (anchorRef.current?.closest('[data-state="open"][role="dialog"]') as HTMLElement) || null;
  }, [anchorRef]);

  /**
   * Sync flatpickr display when value, timezone, or date format changes.
   *
   * config.formatDate is updated first so that any subsequent setDate call
   * uses the correct (potentially new) format string immediately.
   *
   * setDate is always called (no needsSynced guard) so that format-only changes
   * (e.g. switching 12h ↔ 24h) reliably refresh the input text. Flatpickr's
   * setDate/buildAndUpdateOutput is the only path that writes to input.value;
   * set('time_24hr') only redraws the calendar popup, not the text field.
   */
  useEffect(() => {
    if (flatpickrRef.current) {
      flatpickrRef.current.config.formatDate = formatDate;
      if (value) {
        const jsDate = momentToTimezoneNaiveDate(value, timezone);
        flatpickrRef.current.setDate(jsDate, false);
      } else {
        flatpickrRef.current.clear(false);
      }
    }
  }, [value?.valueOf(), timezone, localizedDateFormat, formatDate]);

  useEffect(() => {
    if (flatpickrRef.current) {
      flatpickrRef.current.config.parseDate = parseDate;
      flatpickrRef.current.config.closeOnSelect = !applyOnDone;
      flatpickrRef.current.config.onChange = [handleDateChange];
      flatpickrRef.current.config.onMonthChange = [handleMonthYearChange];
      flatpickrRef.current.config.onYearChange = [handleMonthYearChange];
      flatpickrRef.current.config.formatDate = formatDate;
    }
  }, [applyOnDone, parseDate, handleDateChange, handleMonthYearChange, formatDate]);

  useEffect(() => {
    if (flatpickrRef.current && isOpen !== undefined) {
      if (isOpen) {
        flatpickrRef.current.open();
      } else {
        flatpickrRef.current.close();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    let timeoutId: number | undefined;
    let rafId: number | undefined;

    const clearTimeoutIfSet = () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
        timeoutId = undefined;
      }
    };

    const cancelRaf = () => {
      if (rafId !== undefined) {
        cancelAnimationFrame(rafId);
        rafId = undefined;
      }
    };

    const stopWaitingForAnchor = () => {
      cancelRaf();
      clearTimeoutIfSet();
    };

    const initializeFlatpickr = (): boolean => {
      if (flatpickrRef.current) {
        stopWaitingForAnchor();
        return true;
      }

      const anchor = anchorRef.current;
      if (!anchor?.isConnected) {
        return false;
      }

      const appendTo = isInsideModal ? getModalContainer() : null;
      if (isInsideModal && !appendTo) {
        warnFlatpickrIssue('Unable to find modal container; calendar will use Flatpickr default placement.', {
          anchorTestId: anchor.getAttribute('data-testid'),
        });
      }
      const initialDate = value ? momentToTimezoneNaiveDate(value, timezone) : undefined;

      try {
        flatpickrRef.current = flatpickr(anchor, {
          ...(!!appendTo && {
            appendTo,
            position: (fp: FlatpickrInstance, relativeEl: HTMLElement | undefined) => {
              positionFlatpickrCalendar(fp, relativeEl, appendTo);
            },
          }),
          allowInput: true,
          clickOpens: true,
          // Closing on select would discard a pending selection when the calendar has no time inputs; see handleClose.
          closeOnSelect: !applyOnDone,
          dateFormat: 'Z', // Use ISO format internally
          defaultDate: initialDate,
          disableMobile: true,
          enableSeconds,
          enableTime,
          formatDate,
          parseDate,
          time_24hr: enableTime24hr,
          inline,
          minuteIncrement,
          onChange: handleDateChange,
          onOpen: () => {
            onOpenRef.current?.();
          },
          onClose: handleClose,
          onMonthChange: handleMonthYearChange,
          onYearChange: handleMonthYearChange,
          onReady: handleReady,
          ...(minDate && { minDate: momentToTimezoneNaiveDate(minDate, timezone) }),
          ...(maxDate && { maxDate: momentToTimezoneNaiveDate(maxDate, timezone) }),
        });
      } catch (error) {
        warnFlatpickrIssue('Flatpickr initialization failed.', {
          anchorTestId: anchor.getAttribute('data-testid'),
          error,
        });
        return false;
      }

      if (calendarExtraClassName) {
        flatpickrRef.current.calendarContainer.classList.add(...calendarExtraClassName.split(/\s+/).filter(Boolean));
      }

      stopWaitingForAnchor();
      return true;
    };

    if (initializeFlatpickr()) return;

    const pollForAnchor = () => {
      if (initializeFlatpickr()) return;
      rafId = requestAnimationFrame(pollForAnchor);
    };
    rafId = requestAnimationFrame(pollForAnchor);

    timeoutId = window.setTimeout(() => {
      cancelRaf();
      if (!flatpickrRef.current) {
        const anchor = anchorRef.current;
        warnFlatpickrIssue('Unable to initialize because the anchor element was not connected after waiting.', {
          timeoutMs: INITIALIZATION_TIMEOUT_MS,
          hasAnchor: !!anchor,
          anchorConnected: anchor?.isConnected,
          anchorTestId: anchor?.getAttribute('data-testid'),
        });
      }
    }, INITIALIZATION_TIMEOUT_MS);

    return stopWaitingForAnchor;
  }, [initializationSignal, localizedDateFormat]);

  useEffect(() => {
    return () => {
      buttonCleanupRef.current?.();
      buttonCleanupRef.current = null;
      flatpickrRef.current?.destroy();
      flatpickrRef.current = null;
    };
  }, []);

  return flatpickrRef;
};
